import React from 'react';

// How close to the right edge counts as "the user is asking for more". One card
// width is too late on a fast flick, a few hundred pixels loads before the gap
// is visible.
const LOAD_MORE_THRESHOLD_PX = 320;

/**
 * Calls `onReachEnd` once per arrival near the right edge of a horizontal row,
 * so a caller can append the next page. Re-arms whenever the row grows, so a
 * page that lands without filling the row asks again.
 *
 * Shared by the desktop scroller and the mobile carousel — paging only one of
 * them could do left mobile stuck on the first page of a row.
 */
export function useReachEnd(
  ref: React.RefObject<HTMLElement | null>,
  onReachEnd: (() => void) | undefined,
  children: React.ReactNode,
): void {
  // Latch so one long scroll asks once; cleared when the content grows, which is
  // what arriving cards do.
  const askedRef = React.useRef(false);
  const childCountRef = React.useRef(0);

  React.useEffect(() => {
    const node = ref.current;
    if (!node || !onReachEnd) return undefined;
    const check = () => {
      // Count the cards, not the scroll width: on a viewport wide enough to
      // hold the whole row, a page can arrive without scrollWidth ever
      // exceeding clientWidth, and a width-based latch would never re-arm —
      // leaving the row stuck on its first page exactly where it is most
      // obvious that more should fit.
      if (node.children.length !== childCountRef.current) {
        childCountRef.current = node.children.length;
        askedRef.current = false;
      }
      const remaining = node.scrollWidth - node.clientWidth - node.scrollLeft;
      if (remaining <= LOAD_MORE_THRESHOLD_PX && !askedRef.current) {
        askedRef.current = true;
        onReachEnd();
      }
    };
    node.addEventListener('scroll', check, { passive: true });
    // Widening the viewport shrinks the distance to the end without any scroll
    // happening, so a row that suddenly has room for another page would sit
    // there unasked until the user touched it.
    const observer = new ResizeObserver(check);
    observer.observe(node);
    check();
    return () => {
      node.removeEventListener('scroll', check);
      observer.disconnect();
    };
  }, [ref, onReachEnd, children]);
}

export default useReachEnd;
